/**
 * PATH: packages/ui/src/components/settings/buildSettingsTabs.tsx
 * FILE_ID: UI-SETTINGS-BUILDER-002
 * PURPOSE: Compose the universal settings tab list — ONE tab per surface
 *          (flat sidebar, matches the SAM/Newmark per-tenant override layout).
 * WHY (2026-05-15): flat sidebar — one tab per surface. Organisation hub
 *   (2026-05-21): Details + Entities + Integrations tabified under one
 *   "Organisation" sidebar entry; legacy `?tab=entities` routes still work.
 * FLOW: BuildSettingsTabsOptions → section builders → flat SettingsTab[]
 * DEPENDENCIES:
 *   - settings-tab-sections/{account,preferences,admin}-sections
 * RELATED:
 *   - SettingsModal.tsx (consumer — sidebar grouping + RBAC padlock)
 *   - src/app/layouts/unified-layout/types.ts TAB_ID_MAP (already expects flat ids)
 */

import { Activity, Plug, Settings as SettingsCog, type LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { type SettingsTab, type SettingsUser } from '../SettingsModal'
import type { SettingsSection } from './SectionedContent'
import type { ProfileData } from './ProfileTab'
import type { NotificationsTabProps } from './NotificationsTab'
import type { EmailTabProps } from './EmailTab'
import type { FilesTabProps } from './FilesTab'
import type { NavigationTabProps } from './NavigationTab'
import type { OrganisationData } from './OrganisationTab'
import type { EntitiesTabProps } from './EntitiesTab'
import type { BrandingTabProps } from './BrandingTab'
import { IntegrationsTab, type IntegrationStatus } from './IntegrationsTab'
import type { TeamTabProps } from './TeamTab'
import type { AccessControlTabProps } from './AccessControlTab'
import type { ModuleAccessTabProps } from './ModuleAccessTab'
import type { RoleManagementTabProps } from './RoleManagementTab'
import type { KeyboardShortcut } from './KeyboardShortcutsTab'
import type { SecurityTabProps } from './SecurityTab'
import type { EmailAccountsTabProps } from './EmailAccountsTab'
import type { SettingsApiClient, AiUsageApi } from './types'
import { UsageTab } from './UsageTab'
import { buildAccountSections } from './settings-tab-sections/account-sections'
import { buildPreferencesSections } from './settings-tab-sections/preferences-sections'
import type { OrganisationPanelId } from './organisation-hub-types'
import {
  buildOrganisationHubTab,
  buildBrandingSection,
  buildTeamSections,
  buildAccessSections,
} from './settings-tab-sections/admin-sections'

// =============================================================================
// TYPES
// =============================================================================

export interface BuildSettingsTabsOptions {
  user?: SettingsUser | null
  hubId?: string
  hubName?: string
  extraTabs?: SettingsTab[]
  apiClient?: SettingsApiClient
  organisationId?: string

  // Profile / Password
  profileData?: ProfileData
  onProfileSave?: (data: Partial<ProfileData>) => Promise<void>
  onAvatarChange?: (file: File) => Promise<void>
  profileContent?: ReactNode
  onPasswordChange?: (current: string, newPassword: string) => Promise<void>
  passwordContent?: ReactNode

  // Organisation / Entities / Branding
  organisationData?: OrganisationData
  onOrganisationSave?: (data: Partial<OrganisationData>) => Promise<void>
  organisationContent?: ReactNode
  entitiesProps?: Partial<EntitiesTabProps>
  brandingProps?: Partial<BrandingTabProps>
  isXeroConnected?: boolean
  /**
   * Whether Xero accounting applies to this org (OrganisationSettings.
   * enable_xero). Defaults to true. When false, the Entities tab hides its
   * Xero tenant-mapping coupling so non-Xero orgs (QuickBooks / MYOB / no
   * accounting) aren't funnelled into Xero UI. The Integrations panel keeps
   * the Xero card as the connect / re-enable surface.
   */
  xeroApplicable?: boolean
  onOpenIntegrationsTab?: () => void
  /** Which horizontal panel to show inside the Organisation hub tab */
  organisationInitialPanel?: OrganisationPanelId

  // Preferences
  navigationProps?: Partial<NavigationTabProps>
  notificationsProps?: Partial<NotificationsTabProps>
  emailProps?: Partial<EmailTabProps>
  filesProps?: Partial<FilesTabProps>

  // Integrations
  integrations?: IntegrationStatus[]
  onIntegrationConnect?: (id: string) => Promise<void>
  onIntegrationDisconnect?: (id: string) => Promise<void>
  onIntegrationSync?: (id: string) => Promise<void>
  integrationsContent?: ReactNode
  emailAccountsProps?: Partial<EmailAccountsTabProps>

  // Team / Access Control
  teamProps?: Partial<TeamTabProps>
  accessControlProps?: Partial<AccessControlTabProps>
  moduleAccessProps?: Partial<ModuleAccessTabProps>
  roleManagementProps?: Partial<RoleManagementTabProps>

  // Keyboard
  shortcuts?: KeyboardShortcut[]
  showUniversalShortcuts?: boolean
  keyboardContent?: ReactNode

  // Security
  securityProps?: Partial<SecurityTabProps>

  // AI Usage (host-supplied adapter; tab is conditional)
  aiUsageApi?: AiUsageApi
  aiUsageTitle?: string
  aiUsageSubtitle?: string

  // Loading
  isProfileLoading?: boolean
  isProfileSaving?: boolean
  isIntegrationsLoading?: boolean

  // Visibility
  hideTabs?: string[]
}

// =============================================================================
// SECTION → TAB MAPPING
// =============================================================================

/**
 * Some section ids inside the section builders use longer descriptive
 * names that don't match the flat tab ids the rest of the codebase
 * expects (src/app/layouts/unified-layout/types.ts TAB_ID_MAP, Settings
 * URL routing, hub-level defaultSettingsTab calls, etc.). When a
 * section is promoted to a top-level tab, rename to the canonical id.
 *
 * If you add a new section, add its alias here if its section id
 * differs from the public tab id consumers will route to.
 */
const SECTION_ID_TO_TAB_ID: Record<string, string> = {
  'email-prefs': 'email',
  'team-members': 'team',
  'page-permissions': 'access-control',
}

interface PromoteOptions {
  category: string
  adminOnly?: boolean
  requiredPermission?: { module: string; action: string }
}

function sectionsToTabs(
  sections: SettingsSection[],
  opts: PromoteOptions,
): SettingsTab[] {
  return sections.map((s): SettingsTab => ({
    id: SECTION_ID_TO_TAB_ID[s.id] ?? s.id,
    label: s.title,
    // SettingsSection.icon is optional but SettingsTab.icon is required —
    // every current section sets one explicitly; the fallback covers
    // future sections added without an icon (won't crash the sidebar).
    icon: (s.icon as LucideIcon | undefined) ?? SettingsCog,
    category: opts.category,
    description: s.description,
    adminOnly: opts.adminOnly,
    requiredPermission: opts.requiredPermission,
    content: s.content,
  }))
}

// =============================================================================
// FLAT TAB ASSEMBLER
// =============================================================================

function createDefaultTabs(options: BuildSettingsTabsOptions): SettingsTab[] {
  const ADMIN_GATE = { module: 'settings', action: 'admin' } as const
  const WRITE_GATE = { module: 'settings', action: 'write' } as const

  const tabs: SettingsTab[] = [
    // ── Account (3) — never gated; everyone manages their own profile ──
    ...sectionsToTabs(buildAccountSections(options), { category: 'Account' }),

    // ── Preferences (6) — per-user device/display choices ──
    ...sectionsToTabs(buildPreferencesSections(options), { category: 'Preferences' }),

    // ── Organisation hub (Details + Entities + Integrations) ──
    ...((): SettingsTab[] => {
      const hub = buildOrganisationHubTab(options)
      return hub
        ? [{
            ...hub,
            adminOnly: true,
            requiredPermission: ADMIN_GATE,
          }]
        : []
    })(),

    // ── Integrations (standalone, top-level) ──
    // Discoverability (operator 2026-06-26: "nothing about integrating to
    // Xero in settings"). Integrations/Xero historically lived ONLY as an
    // inner panel of the Organisation hub tab, so it never appeared in the
    // settings sidebar and owners could not find it. Surface a dedicated,
    // clearly-labelled top-level entry that renders the SAME integrations
    // surface. Admin-gated (connecting Xero is owner/admin only — the
    // backend returns 403 to members). Only emitted when the host wires
    // integrations content/data, so hubs without integrations don't get an
    // empty tab (the buildSettingsTabs contract test relies on this).
    ...((): SettingsTab[] => {
      const hasIntegrations = !!options.integrationsContent ||
        (Array.isArray(options.integrations) && options.integrations.length > 0)
      if (!hasIntegrations) return []
      return [{
        id: 'integrations',
        label: 'Integrations',
        icon: Plug,
        category: 'Organisation',
        description: 'Connect Xero and other accounting & sync services',
        adminOnly: true,
        requiredPermission: ADMIN_GATE,
        content: options.integrationsContent ?? (
          <IntegrationsTab
            integrations={options.integrations ?? []}
            isLoading={options.isIntegrationsLoading}
            onConnect={options.onIntegrationConnect}
            onDisconnect={options.onIntegrationDisconnect}
            onSync={options.onIntegrationSync}
          />
        ),
      }]
    })(),

    ...sectionsToTabs(buildBrandingSection(options), {
      category: 'Organisation',
      adminOnly: true,
      requiredPermission: ADMIN_GATE,
    }),

    // ── Team (1) ──
    ...sectionsToTabs(buildTeamSections(options), {
      category: 'Organisation',
      adminOnly: true,
      requiredPermission: ADMIN_GATE,
    }),

    // ── Access Control (3) — RBAC. 'access-control' uses settings.write
    //    because page-level rules are write-tier; the other two require
    //    settings.admin like the rest of Organisation. ──
    ...sectionsToTabs(buildAccessSections(options), {
      category: 'Organisation',
      adminOnly: true,
      requiredPermission: WRITE_GATE,
    }),
  ]

  // Optional Usage tab — only when host app wires up an AiUsageApi
  // adapter + organisationId. Hidden otherwise (previous behaviour).
  if (options.aiUsageApi && options.organisationId) {
    tabs.push({
      id: 'usage',
      label: 'AI Usage',
      icon: Activity,
      category: 'Organisation',
      description: 'AI inference usage and billing',
      adminOnly: true,
      requiredPermission: ADMIN_GATE,
      content: (
        <UsageTab
          api={options.aiUsageApi}
          organisationId={options.organisationId}
          organisationName={options.user?.organisation_name}
          title={options.aiUsageTitle}
          subtitle={options.aiUsageSubtitle}
        />
      ),
    })
  }

  return tabs
}

// =============================================================================
// MAIN EXPORT
// =============================================================================

/**
 * Build the flat list of universal settings tabs.
 *
 * Returns one tab per surface (no collapsible sections). Consumers may
 * pass `extraTabs` to inject hub-specific tabs (overrides any tab with
 * the same id) or `hideTabs` to drop tabs by id.
 */
export function buildSettingsTabs(options: BuildSettingsTabsOptions): SettingsTab[] {
  const { hubId, hubName, extraTabs = [], hideTabs = [] } = options

  let tabs = createDefaultTabs(options)

  // Hub-specific extras. Same id → override; otherwise append.
  if (extraTabs.length > 0) {
    const hubCategory = hubName || (hubId ? `${hubId.toUpperCase()} Hub` : 'Hub Settings')
    extraTabs.forEach((tab) => {
      const resolvedTab: SettingsTab = {
        ...tab,
        category: tab.category || hubCategory,
      }
      const existingIndex = tabs.findIndex((existing) => existing.id === resolvedTab.id)
      if (existingIndex >= 0) tabs[existingIndex] = resolvedTab
      else tabs.push(resolvedTab)
    })
  }

  if (hideTabs.length > 0) {
    tabs = tabs.filter((tab) => !hideTabs.includes(tab.id))
  }

  return tabs
}

export default buildSettingsTabs
